import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import { twMerge } from 'tailwind-merge';
import { labelByKey } from '@cm/new-shared-fe/services/labels';

import { useForkRef } from '../../utils/useForkRef'; // eslint-disable-line no-unused-vars
import { InfoField } from '../../index'; // eslint-disable-line no-unused-vars

/**
 * @typedef {'text' | 'email' | 'url' | 'password' | 'number' | 'date' | 'datetime-local' | 'month' | 'search' | 'tel' | 'time' | 'week'} InputTypes
 */

/**
 * @typedef {{
 *   badInput: boolean,
 *   customError: boolean,
 *   patternMismatch: boolean,
 *   rangeOverflow: boolean,
 *   rangeUnderflow: boolean,
 *   stepMismatch: boolean,
 *   tooLong: boolean,
 *   tooShort: boolean,
 *   typeMismatch: boolean,
 *   valid: boolean,
 *   valueMissing: boolean
 * }} ValidityState
 */

// TODO: add validation
// TODO: disabled styles
// TODO: icon
// TODO: custom select
/**
 * An input component
 * @param {{
 *   className?: string,
 *   disabled?: boolean,
 *   label?: string,
 *   multiline?: boolean,
 *   type?: InputTypes,
 *   validate?: function,
 *   value?: any
 * }} props
 * @return {JSX.Element}
 * @constructor
 */
const InputField = React.forwardRef(function InputField (props, ref) {
  // Alphabetical
  const {
    children,
    className,
    defaultValue,
    disabled,
    formatter = (/** any */ value) => value, // NO-OP
    label,
    multiline = false,
    name,
    onBlur,
    onChange,
    onClick,
    onFocus,
    pattern,
    placeholder,
    required,
    minRows = 2,
    select = false,
    type = 'text',
    validate, // function
    validationText, // for react-hook-form usages: "validationText={errors.email?.type}"
    value: valueProp,
    ...other
  } = props;

  if (process.env.NODE_ENV !== 'production') {
    if (select && !children) {
      console.error(
        'cm/react-components: `children` must be passed when using the `InputField` component with `select`.',
      );
    }
  }

  const [value, setValue] = useState(valueProp || '');
  const [focused, setFocused] = useState(false);
  // set visual error state
  const [error, setError] = useState(false); // eslint-disable-line no-unused-vars
  const [errors, setErrors] = useState({}); // eslint-disable-line no-unused-vars

  // inputRef must be declared here so the ref can refer to it
  const inputRef = useRef(null);
  const handleInputRef = useForkRef(inputRef, ref);

  const _id = `input-field_${uuid()}`;

  React.useEffect(() => {
    setValue(valueProp);
  }, [valueProp]);

  // Check validity
  React.useEffect(() => {
    if (error && typeof ref !== 'function') {
      _checkValidity();
    }
  }, [value, error]);

  const _checkValidity = () => {
    let errors = {};

    // required
    if (required && !value) {
      errors = { ...errors, required: true };
    }

    // If a "validate" function is provided,
    // we add any possible errors to the errors object
    if (validate) {
      // check validity
      const _validationObject = validate(value); // { [validatorName]: true } || {} || null
      errors = { ...errors, ...(_validationObject || {}) };
    }

    setErrors(errors);
    setError(!!Object.keys(errors).length);
  };

  const handleFocus = (/** Event */ event) => {
    if (onFocus) {
      onFocus(event);
    }

    setFocused(true);
  };

  const handleBlur = (/** Event */ event) => {
    if (!value && !placeholder) {
      setFocused(false);
    }

    if (onBlur) {
      onBlur(event);
    }

    if (typeof ref !== 'function') {
      _checkValidity();
    }
  };

  // For react-hook-form usages
  React.useEffect(() => {
    if (validationText) {
      setError(true);
    } else {
      setError(false);
    }
  }, [validationText]);

  const handleChange = (/** Event */ event) => {
    const _value = event.target.value;
    setValue(formatter(_value));

    if (multiline) {
      fitContent(event.target);
    }

    if (onChange) {
      onChange(event);
    }
  };

  // Check the input state on mount, in case it was filled by the user
  React.useEffect(() => {
    if (value || placeholder || select) {
      setFocused(true);
    }
  }, [value, placeholder, select]);

  const handleClick = (event) => {
    if (typeof ref !== 'function' && inputRef.current && event.currentTarget === event.target) {
      inputRef.current.focus();
    }

    if (onClick) {
      onClick(event);
    }
  };

  /* TODO: put inside internal textarea component */
  const fitContent = (/** HTMLElement */ element) => {
    element.style.height = 'auto';
    element.style.height = (element.scrollHeight) + 'px';
  };

  return (
    <>
      <div className={twMerge('relative w-full', className)} onClick={handleClick}>
        {label != null && label !== '' && (
          <label
            className={twMerge(
              'absolute left-3 top-2.5 text-cmblack pointer-events-none bg-white z-1 transition-all',
              focused ? 'text-sm px-1 -translate-y-full' : 'text-md',
            )}
            htmlFor={_id}
          >
            {required ? `${label} *` : label}
          </label>
        )}
        {/* TODO: add internal select component to use here */}
        {select && !multiline && (
          <select
            className="relative w-full pt-3 pb-1.5 border border-solid border-griege rounded focus:ring-0"
            id={_id}
            name={name}
            onChange={handleChange}
            value={value}
          >
            {children}
          </select>
        )}
        {!select && multiline && (
          <textarea
            className={twMerge(
              'relative w-full pt-3 pb-1.5 border border-solid border-griege rounded focus:ring-0',
              !(error || validationText) && value ? 'shadow-focus' : '',
              (error || validationText) && 'shadow-invalid',
            )}
            disabled={disabled}
            id={_id}
            name={name}
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={handleFocus}
            placeholder={placeholder}
            ref={handleInputRef}
            required={required}
            rows={minRows}
            style={{
              height: 'auto',
              minHeight: '44px',
              overflow: 'hidden',
            }}
            value={value}
          ></textarea>
        )}
        {!select && !multiline && (
          <input
            className={twMerge(
              'relative w-full pt-3 pb-1.5 border border-solid border-griege rounded focus:ring-0',
              !(error || validationText) && value ? 'shadow-focus' : '',
              (error || validationText) && 'shadow-invalid',
            )}
            defaultValue={defaultValue}
            disabled={disabled}
            id={_id}
            name={name}
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={handleFocus}
            placeholder={placeholder}
            ref={handleInputRef}
            required={required}
            type={type}
            value={value || ''}
            {...other}
          />
        )}
      </div>
      {!validationText &&
        Object.entries(errors).map(
          ([key, value]) => value && (
            <InfoField key={key} error className="mt-1.5">{labelByKey(`input_field.error.${key}`)}</InfoField>
          ),
        )}
      {validationText && (
        <InfoField error className="mt-1.5">
          TODO: {`input_field.error.${validationText}`}
        </InfoField>
      )}
    </>
  );
});

InputField.propTypes = {
  /**
   * @ignore
   */
  children: PropTypes.node,
  /**
   * Extend the styles applied to the component.
   */
  className: PropTypes.string,
  /**
   * The default value. Use when the component is not controlled.
   */
  defaultValue: PropTypes.any,
  /**
   * If `true`, the component is disabled.
   * @default false
   */
  disabled: PropTypes.bool,
  /**
   * @ignore
   */
  formatter: PropTypes.func,
  /**
   * The label content.
   */
  label: PropTypes.string,
  /**
   * If `true`, a `textarea` element is rendered instead of an input.
   * @default false
   */
  multiline: PropTypes.bool,
  /**
   * Name attribute of the `input` element.
   */
  name: PropTypes.string,
  /**
   * @ignore
   */
  onBlur: PropTypes.func,
  /**
   * Callback fired when the value is changed.
   *
   * @param {object} event The event source of the callback.
   * You can pull out the new value by accessing `event.target.value` (string).
   */
  onChange: PropTypes.func,
  /**
   * @ignore
   */
  onClick: PropTypes.func,
  /**
   * @ignore
   */
  onFocus: PropTypes.func,
  /**
   * The short hint displayed in the `input` before the user enters a value.
   */
  placeholder: PropTypes.string,
  /**
   * If `true`, the label is displayed as required and the `input` element is required.
   * @default false
   */
  required: PropTypes.bool,
  /**
   * Render a `Select` element while passing the Input element to `Select` as `input` parameter.
   * If this option is set you must pass the options of the select as children.
   * @default false
   */
  select: PropTypes.bool,
  /**
   * Type of the `input` element. It should be [a valid HTML5 input type](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#Form_%3Cinput%3E_types).
   */
  type: PropTypes.string,
  /**
   * Function that is called when the value has changed.
   *
   * @param {any} value The value to be checked.
   * @return {{[key]: boolean}|{}|null} This function should return an object with one or more errors, an empty object or null.
   */
  validate: PropTypes.func,
  /**
   * The value of the `input` element, required for a controlled component.
   */
  value: PropTypes.any,
};

export default InputField;
